/**
 * Public /validate/success — recipient viewer (Bucket Q2.5).
 *
 * Lands here after the magic-link confirm endpoint has set the share-
 * session cookie. Reads grant_id from the URL query string (set by the
 * backend redirect), fetches the payload via the cookie, and renders a
 * minimal report view. Q3 will swap this for the watermarked-PDF
 * pipeline + the stakeholder-pack templates from Q4.
 *
 * Recipients are NOT in the portal — there's no portal nav, no auth
 * shell. The page is its own surface.
 */
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { getValidatedPayloadApiV1ValidateGrantIdPayloadGet } from "@hackereyes/api-client";
import "./validate-success-page.css";

interface PayloadShape {
  report?: { title?: string };
  case?: { case_number?: string };
  total_findings?: number;
  severity_counts?: {
    critical: number;
    high: number;
    medium: number;
    low: number;
    informational: number;
  };
  security_grade?: { grade: string; summary: string };
  exec_summary_paragraphs?: string[];
  attacker_narrative?: string | null;
  action_plan_html?: string;
  _signature?: {
    signed_at: string;
    payload_content_hash: string;
    verify_path: string;
  } | null;
}

export function ValidateSuccessPage() {
  const [params] = useSearchParams();
  const grantId = params.get("grant");

  const { data, isLoading, error } = useQuery({
    queryKey: ["validate-payload", grantId],
    enabled: !!grantId,
    queryFn: async () => {
      const { data, error } =
        await getValidatedPayloadApiV1ValidateGrantIdPayloadGet({
          path: { grant_id: grantId! },
        });
      if (error) {
        throw new Error(
          ((error as Record<string, unknown>)?.detail as string) || "load_failed",
        );
      }
      return data as unknown as PayloadShape;
    },
  });

  if (!grantId) {
    return (
      <div className="vsp-page">
        <div className="vsp-fallback">
          <h1>Verification needed</h1>
          <p>
            This URL is missing the grant identifier. Make sure you
            arrived here via the confirmation link in your email — the
            link sets a session cookie that lets you view the report.
            If you're stuck,{" "}
            <a href="/validate">start the validation flow over</a>.
          </p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="vsp-page">
        <div className="vsp-loading">Loading report…</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="vsp-page">
        <div className="vsp-fallback">
          <h1>Couldn't load this report</h1>
          <p>
            Your session may have expired or the share may have been
            revoked. <a href="/validate">Verify again</a> if you have a
            fresh token.
          </p>
        </div>
      </div>
    );
  }

  const sev = data.severity_counts ?? {
    critical: 0, high: 0, medium: 0, low: 0, informational: 0,
  };
  const total = data.total_findings ?? 0;

  return (
    <div className="vsp-page">
      <div className="vsp-inner">
        <header className="vsp-header">
          <div className="eyebrow">Shared via /Recon</div>
          <h1 className="vsp-title">{data.report?.title || "Assessment report"}</h1>
          <div className="vsp-meta-row">
            <span className="vsp-meta-pill">{data.case?.case_number || "—"}</span>
            {data._signature && (
              <a
                className="vsp-meta-pill vsp-meta-pill-verified"
                href={data._signature.verify_path}
                target="_blank"
                rel="noreferrer"
                title={`Signed ${new Date(data._signature.signed_at).toLocaleString()}\nHash: ${data._signature.payload_content_hash}`}
              >
                ✓ Verified
              </a>
            )}
            <a
              className="vsp-pdf-link"
              href={`/api/v1/validate/${grantId}/pdf`}
              target="_blank"
              rel="noreferrer"
            >
              Download PDF
            </a>
          </div>
        </header>

        {data.security_grade && (
          <section className="vsp-grade-card">
            <div className="vsp-grade-letter">{data.security_grade.grade}</div>
            <div className="vsp-grade-summary">
              {data.security_grade.summary}
            </div>
          </section>
        )}

        <section className="vsp-severity">
          <h2 className="vsp-section-title">Findings</h2>
          <div className="vsp-severity-strip">
            <Tile label="Critical" value={sev.critical} tone="critical" />
            <Tile label="High" value={sev.high} tone="high" />
            <Tile label="Medium" value={sev.medium} tone="medium" />
            <Tile label="Low" value={sev.low} tone="low" />
            <Tile label="Informational" value={sev.informational} tone="info" />
            <Tile label="Total" value={total} tone="total" />
          </div>
        </section>

        {data.attacker_narrative && (
          <section className="vsp-section">
            <h2 className="vsp-section-title">What an attacker sees first</h2>
            <p className="vsp-narrative">{data.attacker_narrative}</p>
          </section>
        )}

        {data.exec_summary_paragraphs && data.exec_summary_paragraphs.length > 0 && (
          <section className="vsp-section">
            <h2 className="vsp-section-title">Executive summary</h2>
            {data.exec_summary_paragraphs.map((p, i) => (
              <p key={i} className="vsp-narrative">{p}</p>
            ))}
          </section>
        )}

        {data.action_plan_html && (
          <section className="vsp-section">
            <h2 className="vsp-section-title">Risk summary &amp; recommendations</h2>
            <div
              className="vsp-action-plan"
              dangerouslySetInnerHTML={{ __html: data.action_plan_html }}
            />
          </section>
        )}

        <footer className="vsp-footer">
          <p>
            This report was shared with you by an authenticated
            SignumCyber client. Every view + download on this share is
            audit-logged. Click "Verified" above to independently
            confirm the canonical record's signature.
          </p>
        </footer>
      </div>
    </div>
  );
}

function Tile({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: string;
}) {
  return (
    <div className={`vsp-tile vsp-tile-${tone}`}>
      <div className="vsp-tile-value">{value}</div>
      <div className="vsp-tile-label">{label}</div>
    </div>
  );
}
